<template>
  <div>
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>

    <div class="patient-details-container">
      <div class="data-processing-container">
        <div style="font-weight: bold; font-size: 15px">
          {{ patient.patient_identifier }}
        </div>
        <!-- ------------------------------------------Facescan Section-------------------------------------------------- -->
        <div v-if="texture && mesh" class="facescan-list">
          <table class="table table-bordered table-striped facescan-table">
            <thead>
              <tr>
                <th>Scan Date</th>
                <th>Imaging Modality</th>
                <th>Mesh File</th>
                <th>Texture File</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ scanDetails.scanDate }}</td>
                <td>{{ scanDetails.imagingModality }}</td>
                <td>
                  <div v-if="mesh">
                    <div id="3d-viewer"></div>
                  </div>
                </td>
                <td>
                  <div v-if="texture">
                    <img
                      :src="texture"
                      alt="Texture File (PNG)"
                      class="texture"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Loading State -->
        <p v-if="loading" class="loading-message">
          Loading texture and mesh...
        </p>

        <!-- Error State -->
        <p v-if="error" class="error-message">{{ error }}</p>

        <!-- Landmark Records Section -->
        <div
          v-if="landmarkRecords.length > 0 && !hideLandmarksUntilDone"
          class="mt-4"
        >
          <div class="landmarktitle font-weight-bold">Landmark Records</div>

          <div class="form-container">
            <div class="landmarks-list mt-2">
              <div
                v-for="record in landmarkRecords"
                :key="record.id"
                class="landmarksrecord-row d-flex align-items-center mb-2 p-2"
                @click="onSelectLandmark(record)"
              >
                <div class="col-5">
                  <span class="created-date font-weight-bold">Algorithm:</span>
                  <span>{{ getAlgorithmDisplayName(record.algorithm) }}</span>
                </div>
                <div class="col-5">
                  <span class="created-date font-weight-bold"
                    >Created Date:</span
                  >
                  <span>{{
                    new Date(record.created_at).toLocaleString()
                  }}</span>
                </div>
                <div
                  class="col-2 d-flex justify-content-center align-items-center"
                >
                  <i
                    class="fa fa-trash delete-icon-landmark ml-auto"
                    @click.stop="deleteLandmark(record.id)"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Start Analysis Button -->
        <div v-else class="mt-4">
          <button
            v-if="!showProgressStatus && !errorMessage"
            @click="startLandmarkProcess"
            class="buttonDataProcessing"
          >
            <span style="font-size: 15px">EXECUTE FULL WORKFLOW</span>
          </button>
          <div v-if="showProgressStatus" style="max-width: 500px" class="mt-3">
            <!-- Top message -->
            <div class="font-weight-bold mb-2" style="color: #2c3772">
              <i class="fa fa-clock-o" style="color: #2c3772"></i>
              Please wait while both landmark processes are being completed.
            </div>

            <!-- Initial landmark status -->
            <div
              class="alert alert-info d-flex align-items-center mb-2 py-2 px-3"
            >
              <i
                v-if="!initialLandmarksDone"
                class="fa fa-spinner fa-spin mr-2 fa-spinner-icon"
              ></i>
              <i v-else class="fa fa-check-circle text-success mr-2"></i>
              <span>{{ initialLandmarkMessage }}</span>
            </div>

            <!-- Meshmonk status -->
            <div v-if="initialLandmarksDone" class="alert alert-info py-2 px-3">
              <!-- Status icon and message -->
              <div class="d-flex align-items-center mb-2">
                <i
                  v-if="!meshmonkLandmarksDone"
                  class="fa fa-spinner fa-spin mr-2 fa-spinner-icon"
                ></i>
                <i v-else class="fa fa-check-circle text-success mr-2"></i>
                <span>{{ meshmonkMessage }}</span>
              </div>

              <!-- Progress bar shown only during Meshmonk process -->
              <div v-if="!meshmonkLandmarksDone" class="w-100">
                <div class="progress" style="height: 10px">
                  <div
                    class="progress-bar progress-bar-striped progress-bar-animated"
                    role="progressbar"
                    :style="{ width: progressMeshmonk + '%' }"
                    :aria-valuenow="progressMeshmonk"
                    aria-valuemin="0"
                    aria-valuemax="100"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error message and button returns -->
          <div
            v-if="errorMessage"
            class="alert alert-danger alert-dismissible mt-3 d-flex align-items-center"
            role="alert"
            style="max-width: 500px"
          >
            <i
              class="fa fa-warning heatmap_error mr-1"
              style="font-size: 1rem"
            ></i>
            <span class="flex-grow-1">{{ errorMessage }}</span>
            <button
              @click="clearErrorMessage"
              style="
                position: absolute;
                top: 5px;
                right: 10px;
                background: none;
                border: none;
                font-size: 1.4em;
                cursor: pointer;
              "
              aria-label="Close"
              class="AlertCloseButton"
            >
              &times;
            </button>
          </div>
        </div>

        <!-- Display OBJ with VtkObjComponent -->
        <div v-if="displayFetchObjFile" class="mt-4">
          <VtkObjComponent
            :key="vtkComponentKey"
            ref="vtkObjComponent"
            :meshFileURL="mesh"
            :textureFileURL="texture"
            :viewRightEye="view_right_eye"
            :viewLeftEye="view_left_eye"
            :points="
              Object.values(eyeLandmarksData).filter(
                (point) => Array.isArray(point) && point.length === 3,
              )
            "
            @snapshotsCaptured="snapshotsCaptured"
          />
          <!-- ------------------------------------------PFL Section-------------------------------------------------- -->
          <div class="pfl-json">
            <table
              v-if="pflAnalysisResult"
              class="table table-bordered table-striped"
            >
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Value (mm)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="pflAnalysisResult?.PFL !== undefined">
                  <td>Subject PFL</td>
                  <td>
                    {{
                      pflAnalysisResult?.PFL !== undefined
                        ? pflAnalysisResult?.PFL.toFixed(2)
                        : "N/A"
                    }}
                  </td>
                </tr>
                <tr v-if="pflAnalysisResult?.PFLzScore !== undefined">
                  <td>PFL Z-score</td>
                  <td>
                    {{
                      pflAnalysisResult?.PFLzScore !== undefined
                        ? pflAnalysisResult?.PFLzScore.toFixed(2)
                        : "N/A"
                    }}
                  </td>
                </tr>
                <tr v-if="pflAnalysisResult?.PFLpercentile !== undefined">
                  <td>PFL Percentile</td>
                  <td>
                    {{
                      pflAnalysisResult?.PFLpercentile !== undefined
                        ? pflAnalysisResult?.PFLpercentile.toFixed(2)
                        : "N/A"
                    }}
                  </td>
                </tr>
              </tbody>
            </table>

            <!-- Chart Component -->
            <div class="">
              <PFLChart
                ref="PFLChartComponentRef"
                :pflAnalysisResult="pflAnalysisResult"
                :sex="patientSex"
                class="mt-3"
              />
            </div>
          </div>
          <div
            v-if="statusMessage?.show"
            :class="statusMessage.class"
            role="alert"
          >
            <i :class="statusMessage.icon_class" aria-hidden="true"></i>
            {{ statusMessage.icon_title }}
          </div>
          <!-- ------------------------------------------Mandible & Maxillary Section-------------------------------------------------- -->
          <div class="pfl-json">
            <table
              v-if="
                Number.isFinite(MandibleArc) && Number.isFinite(MaxillaryArc)
              "
              class="table table-bordered table-striped"
            >
              <thead>
                <tr>
                  <th>Parameter</th>
                  <th>Value (mm)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Mandible Arc</td>
                  <td>{{ MandibleArc.toFixed(2) }}</td>
                </tr>
                <tr>
                  <td>Maxillary Arc</td>
                  <td>{{ MaxillaryArc.toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <MandibleMaxillaryChart
            v-if="
              arcChartData &&
              patientAge != null &&
              Number.isFinite(MandibleArc) &&
              Number.isFinite(MaxillaryArc)
            "
            :chartData="arcChartData"
            :patientAge="patientAge"
            :subjectMandibleArc="MandibleArc"
            :subjectMaxillaryArc="MaxillaryArc"
            @arcs_zpercentile="updateArcResults"
          />

          <!-- ------------------------------------------Heatmap Section-------------------------------------------------- -->
          <HeatmapComponent
            ref="heatmapComponentRef"
            v-if="showHeatmap"
            :polydataXML="polydataXML"
          />

          <!-- Heatmap Error Alert -->
          <div
            v-if="ErrorAlert.show"
            :class="ErrorAlert.class"
            style="margin: 10px auto 0 auto; text-align: left"
          >
            <i :class="ErrorAlert.icon_class" aria-hidden="true"></i>
            <strong>{{ ErrorAlert.icon_title }}</strong>
            <span style="margin-left: 3px">{{ ErrorAlert.message }}</span>
            <button
              @click="closeErrorAlert"
              style="
                position: absolute;
                top: 5px;
                right: 10px;
                background: none;
                border: none;
                font-size: 1.4em;
                cursor: pointer;
              "
              aria-label="Close"
              class="AlertCloseButton"
            >
              &times;
            </button>
          </div>

          <!-- ------------------------------------------Classification Section--------------------------------------------- -->
          <ClassificationComponent
            ref="ClassificationComponentRef"
            v-show="showClassification"
            :chosenLandmarkId="chosenLandmarkId"
            :pflAnalysisResult="pflAnalysisResult"
            :arcAnalysisResult="arcAnalysisResult"
            @classificationSnapshotCaptured="updateClassificationSnapshot"
            @regionFetched="handleRegionFetched"
            @classificationMeansUpdated="updateClassificationMeans"
          />

          <!-- ------------------------------------------ Mode1Plot Section -------------------------------------------------- -->
          <Mode1Plot
            ref="Mode1PlotComponentRef"
            :patientSex="patientSex"
            :showMode1Plot="showMode1Plot"
            :chosenLandmarkId="chosenLandmarkId"
            :calculateAge="calculateAge"
            :crossImage="crossImage"
            @updateMode1PlotImage="updateMode1PlotImage"
            @Mode1Parameters="Mode1Parameters"
          />

          <!----------------------------------------- Action Buttons -------------------------------------------------------- -->
          <div class="button-group mt-4 d-flex justify-content-between">
            <button
              v-if="displayGenerateArcs"
              class="btn flex-fill mx-1 d-flex justify-content-center align-items-center"
            >
              <span v-if="!showLoadingArcs"></span>
              <span v-else class="d-flex align-items-center">
                <span
                  class="spinner-border spinner-border-sm me-3 mr-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Mandible & Maxillary arcs are being generated…
              </span>
            </button>
            <button
              v-if="displayGenerateHeatMap"
              class="btn flex-fill mx-1 d-flex justify-content-center align-items-center"
            >
              <span v-if="!showLoadingHeatmap"></span>
              <span v-else class="d-flex align-items-center">
                <span
                  class="spinner-border spinner-border-sm me-3 mr-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Heatmap is being generated…
              </span>
            </button>
            <button
              v-if="displayGenerateClassification"
              class="btn flex-fill mx-1 d-flex justify-content-center align-items-center"
            >
              <span v-if="!showLoadingClassification"></span>
              <span v-else class="d-flex align-items-center">
                <span
                  class="spinner-border spinner-border-sm me-3 mr-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Classification Results are being generated…
              </span>
            </button>
          </div>

          <!-- Classification Success Message -->
          <div
            v-if="successMessageClassification"
            class="success-messageClassification"
          >
            <div class="icon">✔</div>
            {{ successMessageClassification }}
          </div>
          <!-- -------------------------------------------Report Section-------------------------------------------------- -->
          <div
            class="button-group button-report d-flex justify-content-between mt-4"
          >
            <button
              @click="onMainReportClick"
              class="btn flex-fill mx-1"
              :class="{}"
              :disabled="!displayGenerateReport"
            >
              <span v-if="!showMainReportLoading">GENERATE REPORT</span>
              <span
                v-else
                class="spinner-border spinner-border-sm ml-2"
                role="status"
                aria-hidden="true"
              ></span>
            </button>
          </div>

          <GeneratePDF
            ref="generatepdf"
            :patient="patient"
            :facescan_id="facescan_id"
            :scanDate="scanDetails.scanDate"
            :imagingModality="scanDetails.imagingModality"
            :snapshots="snapshot_images"
            :pflAnalysisResult="pflAnalysisResult"
            :malePFLChartOptions="malePFLChartOptions"
            :femalePFLChartOptions="femalePFLChartOptions"
            :mode1PlotImage="mode1PlotImage"
            :firstPrincipalComponent="firstPrincipalComponent"
            :mode1Percentile="mode1Percentile"
            :classificationSnapshot="classificationSnapshot"
            :heatmapSnapshot="heatmapSnapshot"
            :landmark_id="landmark_id"
          />

          <ReportComponent
            v-if="showMainReport"
            :patient="patient"
            :scanDate="scanDetails.scanDate"
            :imagingModality="scanDetails.imagingModality"
            :facescan_id="facescan_id"
            :pflAnalysisResult="pflAnalysisResult"
            :showMainReport="showMainReport"
            :snapshots="snapshot_images"
            :mode1PlotImage="mode1PlotImage"
            :heatmapSnapshot="heatmapSnapshot"
            :firstPrincipalComponent="firstPrincipalComponent"
            :mode1Percentile="mode1Percentile"
            :malePFLChartOptions="malePFLChartOptions"
            :femalePFLChartOptions="femalePFLChartOptions"
            :classificationSnapshot="classificationSnapshot"
          />
          <GenerateExcel
            ref="generateexcel"
            :patient="patient"
            :facescan_id="facescan_id"
            :landmark_id="landmark_id"
            :scanDate="scanDetails.scanDate"
            :imagingModality="scanDetails.imagingModality"
            :pflAnalysisResult="pflAnalysisResult"
            :MandibleArc="MandibleArc"
            :MaxillaryArc="MaxillaryArc"
            :classificationMeans="classificationMeans"
          />
          <div v-if="displayDownloadPDF" class="download-btn-container">
            <button class="download-btn" @click="generatePDF">
              <i
                class="fa fa-download"
                aria-hidden="true"
                style="color: white; font-size: 12px"
              ></i>
              Report (.pdf)
            </button>
            <button class="download-btn" @click="generateExcel">
              <i
                class="fa fa-download"
                aria-hidden="true"
                style="color: white; font-size: 12px"
              ></i>
              Report (.xlsx)
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import BreadCrumb from "@/components/BreadCrumb";
import HeatmapComponent from "../dataProcessing/HeatmapComponent.vue";
import ClassificationComponent from "../dataProcessing/ClassificationComponent.vue";
import Mode1Plot from "../dataProcessing/Mode1PlotComponent.vue";
import ReportComponent from "../dataProcessing/ReportComponent.vue";
import GeneratePDF from "./PDFComponent.vue";
import GenerateExcel from "./ExcelComponent.vue";
import PFLChart from "../dataProcessing/PFLChartComponent.vue";
import VtkObjComponent from "../dataProcessing/ThreejsObjComponent.vue";
import MandibleMaxillaryChart from "./MandibleMaxillaryChart.vue";
import { useDPStore } from "@/stores/dataProcessing";

export default {
  name: "DataProcessingComponent",
  components: {
    BreadCrumb,
    VtkObjComponent,
    PFLChart,
    HeatmapComponent,
    ClassificationComponent,
    Mode1Plot,
    ReportComponent,
    GeneratePDF,
    GenerateExcel,
    MandibleMaxillaryChart,
  },
  data() {
    return {
      patient: {
        name: "Unknown",
        surname: "Unknown",
        sex: "Unknown",
        dob: "Unknown",
        ethnicity: "Unknown",
        patient_identifier: "Unknown",
      },
      texture: null,
      mesh: null,
      loading: false,
      error: null,
      scanDetails: {
        scanDate: "",
        imagingModality: "",
      },
      landmarkRecords: [],
      showLandmarksRecord: false,
      showLoadingLandMark: false,
      eyeLandmarksData: {},
      view_right_eye: null,
      view_left_eye: null,
      vtkComponentKey: 0,
      displayFetchObjFile: false,
      snapshot_images: [],
      landmark_id: null,
      patientSex: null,
      pflAnalysisResult: null,
      MandibleArc: null,
      MaxillaryArc: null,
      classificationMeans: null,
      showHeatmap: false,
      showClassification: false,
      polydataXML: "",
      showMode1Plot: false,
      crossImage: require("@/assets/cross.png"),
      mode1PlotImage: null,
      firstPrincipalComponent: null,
      mode1Percentile: null,
      showMainReportLoading: false,
      statusMessageLandmarks: "",
      hideLandmarksUntilDone: false,
      initialLandmarksDone: false,
      meshmonkLandmarksDone: false,
      showProgressStatusLandmark: false,
      initialLandmarkMessage: "Generating initial landmarks...",
      meshmonkMessage: "Generating Meshmonk face map...",
      progressMeshmonk: 0,
      progressInterval: null,
      showMainReport: false,
      errorMessage: null,
      statusMessage: "",
      ErrorAlert: {
        class: "alert alert-danger",
        icon_class: "fa fa-warning heatmap_error",
        show: false,
        icon_title: "",
        message: "",
      },
      arcChartData: null,
      patientAge: null,
      arcAnalysisResult: {
        mandible: null,
        maxillary: null,
      },
      //buttons
      displayGenerateHeatMap: false,
      displayGenerateArcs: false,
      displayGenerateMode1Plot: false,
      displayGenerateReport: false,
      displayDownloadPDF: false,
      showLoadingHeatmap: false,
      showLoadingArcs: false,
      showLoadingClassification: false,
      malePFLChartOptions: null,
      femalePFLChartOptions: null,
      classificationSnapshot: null,
      successMessageClassification: null,
    };
  },
  created() {
    const facescanId = this.$route.params.facescanId;
    const storedPatient = sessionStorage.getItem("patient");

    if (!facescanId) {
      console.error("No facescanId found in route params.");
      return;
    }

    this.facescan_id = facescanId;

    try {
      this.patient = storedPatient ? JSON.parse(storedPatient) : null;
      this.patientSex = this.patient?.sex?.toLowerCase() || null;
    } catch (error) {
      console.error("Failed to parse patient data from sessionStorage:", error);
      this.patient = null;
    }

    this.GetFaceScanData(facescanId)
      .then(() => this.GetFaceScan3DData(facescanId))
      .catch((error) => console.error("Error during facescan init:", error));

    // 2) Try landmark cache first
    const dp = useDPStore();
    const cache = dp.getCache(facescanId);
    const TTL_MS = 15 * 60 * 1000; // 15 minutes
    const isFresh = cache.cachedAt && Date.now() - cache.cachedAt < TTL_MS;

    if (isFresh && cache.landmarkRecords?.length) {
      console.log(
        `[CACHE HIT] Using ${cache.landmarkRecords.length} landmark records from cache for facescanId=${facescanId}`,
      );
      this.allLandmarkRecords = cache.landmarkRecords;
      this.landmarkRecords = cache.landmarkRecords.filter((record) => {
        const alg = record.algorithm.toLowerCase();
        return !alg.includes("facealignment") && !alg.includes("consensus");
      });
      this.showLandmarksRecord = this.landmarkRecords.length > 0;
    } else {
      console.log(
        `[CACHE MISS] Fetching landmark records from API for facescanId=${facescanId}`,
      );
      this.fetchLandmarksData(); // only fetch when cache is missing
    }
  },
  mounted() {
    this._ttlTimer = setInterval(() => {
      const dp = useDPStore();
      const cache = dp.getCache(this.facescan_id);
      const TTL_MS = 15 * 60 * 1000;
      if (cache.cachedAt && Date.now() - cache.cachedAt >= TTL_MS) {
        dp.clearFacescan(this.facescan_id);
      }
    }, 60 * 1000);
  },
  beforeUnmount() {
    clearInterval(this._ttlTimer);
  },

  methods: {
    clearErrorMessage() {
      this.errorMessage = null;
    },
    ///////////////////////////////////////////////////////////////Facescan////////////////////////////////////////////////////////////
    async GetFaceScan3DData(facescanId) {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      try {
        this.loading = true;

        // Fetch Texture
        const textureResponse = await fetch(
          `${fasdBackendURL}/api/facescans/facescantexture/${facescanId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!textureResponse.ok) {
          throw new Error(`HTTP error! status: ${textureResponse.status}`);
        }

        const textureBlob = await textureResponse.blob();
        this.texture = URL.createObjectURL(textureBlob);

        // Fetch Mesh
        const meshResponse = await fetch(
          `${fasdBackendURL}/api/facescans/facescanmesh/${facescanId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!meshResponse.ok) {
          throw new Error(`HTTP error! status: ${meshResponse.status}`);
        }

        const meshBlob = await meshResponse.blob();
        this.mesh = URL.createObjectURL(meshBlob);

        // Wait for DOM updates
        this.$nextTick(() => {
          this.init3DViewer();
        });
      } catch (error) {
        this.error = `Error fetching face scan data: ${error.message}`;
        console.error("Error:", error);
      } finally {
        this.loading = false;
      }
    },

    init3DViewer() {
      const container = document.getElementById("3d-viewer");
      if (!container) {
        console.error("3D Viewer container not found.");
        return;
      }

      container.style.width = "150px";
      container.style.height = "120px";

      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }

      // Initialize Three.js scene
      const scene = new THREE.Scene();
      scene.background = new THREE.Color(0xffffff);
      const camera = new THREE.PerspectiveCamera(
        75,
        container.clientWidth / container.clientHeight,
        0.1,
        1000,
      );
      const renderer = new THREE.WebGLRenderer({
        antialias: true,
        preserveDrawingBuffer: true,
      });
      renderer.setSize(container.clientWidth, container.clientHeight);
      container.appendChild(renderer.domElement);

      const canvas = renderer.domElement;
      canvas.style.borderRadius = "15px";
      canvas.style.backgroundColor = "white";
      canvas.style.boxShadow = "0 4px 8px rgba(0, 0, 0, 0.2)";

      const light = new THREE.DirectionalLight(0xffffff, 1);
      light.position.set(1, 1, 1).normalize();
      scene.add(light);

      const loader = new OBJLoader();
      loader.load(
        this.mesh,
        (object) => {
          object.position.set(0, 0, 0);
          object.scale.set(0.018, 0.017, 0.017);
          scene.add(object);
          console.log("OBJ file loaded successfully.");
        },
        undefined,
        (error) => {
          console.error("Error loading OBJ file:", error);
        },
      );

      camera.position.z = 5;

      const animate = () => {
        requestAnimationFrame(animate);
        renderer.render(scene, camera);
      };

      animate();
    },
    async GetFaceScanData(facescanId) {
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");
        const response = await fetch(
          `${fasdBackendURL}/api/facescans/${facescanId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Parse the response JSON
        const data = await response.json();
        this.scanDetails = {
          scanDate: data.scan_date,
          imagingModality: data.imaging_modality,
        };
        this.patient.id = data.patient_id;
        this.mesh = data.mesh;
        this.texture = data.texture;

        console.log("Face scan data fetched successfully:", data);
      } catch (error) {
        console.error("Error fetching face scan data:", error);
        this.error = "Unable to fetch face scan data.";
      }
    },
    snapshotsCaptured(snapshots) {
      this.snapshot_images = snapshots;
    },

    ///////////////////////////////////////////////////////////////PFL////////////////////////////////////////////////////////////
    async calculateAge() {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");
      const response = await fetch(
        `${fasdBackendURL}/api/facescans/patientAge/${this.facescan_id}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const age = await response.json();
      const PatientAge = parseFloat(age).toFixed(1);
      return PatientAge;
    },
    async onPFLClick() {
      if (!this.patientSex) {
        console.error("Patient sex is not available", this.patient);
        return;
      }

      const sex = this.patientSex;
      const age = await this.calculateAge();
      this.patientAge = Number(age);

      const chosenLandmark = this.landmarkRecords.find((rec) =>
        rec.algorithm.includes("CNNeyeZeyu"),
      );

      if (!chosenLandmark) {
        console.error("No landmark record found with algorithm 'CNNeyeZeyu'");
        return;
      }
      const PFLLandmarkId = chosenLandmark.id;
      this.pflLoading = true;
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      try {
        const response = await fetch(
          `${fasdBackendURL}/api/PFL/PFLmeasures/${PFLLandmarkId}?sex=${sex}&age=${age}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(
              "PFL resource not found (404). Please check the landmark ID or server.",
            );
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const pflResult = await response.json();
        console.log("pflResult", pflResult);

        const PFL = pflResult.PFL;
        const newPoint = [Number(age), PFL];

        console.log("newPoint", newPoint);

        this.pflAnalysisResult = pflResult;
        await this.$nextTick();

        console.log("pflAnalysisResult", this.$refs.PFLChartComponentRef);

        if (this.$refs.PFLChartComponentRef) {
          this.$refs.PFLChartComponentRef.addPoint(newPoint);

          if (sex === "male") {
            this.malePFLChartOptions = JSON.parse(
              JSON.stringify(this.$refs.PFLChartComponentRef.pflChart),
            );
          } else if (sex === "female") {
            this.femalePFLChartOptions = JSON.parse(
              JSON.stringify(this.$refs.PFLChartComponentRef.pflChart),
            );
          }
        } else {
          console.error("PFLChartComponentRef is not available.");
        }

        this.pflLoading = false;
        this.displayGenerateArcs = true;
        this.displayGenerateReport = true;

        this.onMandible();
      } catch (err) {
        console.error("Error in PFL analysis results:", err);
        this.statusMessage = {
          class: "alert alert-danger",
          icon_class: "fa fa-times-circle",
          icon_title: `PFL analysis failed: ${err.message}`,
          show: true,
        };
        this.pflLoading = false;
      }
    },

    initCharts() {
      if (this.$refs.PFLChartComponentRef) {
        this.$refs.PFLChartComponentRef.handleChart();
      } else {
        console.error("PFLChartComponentRef is not available");
      }
    },

    ///////////////////////////////////////////////////////////////Mandible Arc ////////////////////////////////////////////////////////////
    updateArcResults(payload) {
      this.arcAnalysisResult = payload;
    },
    async loadArcChartData() {
      try {
        const response = await fetch("/arcChartData.json");
        if (!response.ok) {
          throw new Error("Failed to load arcChartData.json");
        }
        this.arcChartData = await response.json();
      } catch (error) {
        console.error("Error loading arc chart data:", error);
        this.arcChartData = null;
      }
    },
    async onMandible() {
      this.showLoadingArcs = true;
      const chosenLandmark = this.landmarkRecords.find((rec) =>
        rec.algorithm.includes("Meshmonk"),
      );

      if (!chosenLandmark) {
        console.error("No Meshmonk landmark record found'");
        return;
      }
      const landmarkId = chosenLandmark.landmark_id ?? chosenLandmark.id;
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      try {
        const response = await fetch(
          `${fasdBackendURL}/api/mandible/computeMandibleArc/${landmarkId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(
              "Mandible resource not found (404). Please check the landmark ID or server.",
            );
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const data = await response.json();
        this.MandibleArc = Number(data.total_distance ?? NaN);

        await this.onMaxillary();
        await this.$nextTick();

        await new Promise((r) => requestAnimationFrame(r));
      } catch (err) {
        console.error(err);
        this.errorMessage = err?.message || "Mandible request failed.";
        this.showLoadingArcs = false;
      }
    },

    ///////////////////////////////////////////////////////////////Maxillary Arc ////////////////////////////////////////////////////////////
    async onMaxillary() {
      const chosenLandmark = this.landmarkRecords.find((rec) =>
        rec.algorithm.includes("Meshmonk"),
      );

      if (!chosenLandmark) {
        console.error("No Meshmonk landmark record found'");
        return;
      }
      const landmarkId = chosenLandmark.landmark_id ?? chosenLandmark.id;
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      try {
        const response = await fetch(
          `${fasdBackendURL}/api/maxillary/computeMaxillaryArc/${landmarkId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error(
              "Mandible resource not found (404). Please check the landmark ID or server.",
            );
          } else {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        }

        const data = await response.json();
        this.MaxillaryArc = Number(data.total_distance ?? NaN);
        this.displayGenerateHeatMap = true;
        this.displayGenerateReport = true;
        this.displayGenerateArcs = false;

        await this.loadArcChartData();
        await this.onGenerateHeatmap();
        await this.$nextTick();

        await new Promise((r) => requestAnimationFrame(r));
      } catch (err) {
        console.error(err);
        this.errorMessage = err?.message || "Mandible request failed.";
      } finally {
        this.showLoadingArcs = true;
      }
    },
    ///////////////////////////////////////////////////////////////Landmark////////////////////////////////////////////////////////////
    async onSelectLandmark(record) {
      console.log("Selected Landmark Record:", record);
      this.landmark_id = record.id;
      if (!record.landmarks) {
        console.warn("No landmarks data available for this record");
        return;
      }

      // Parse the landmarks data
      this.eyeLandmarksData = record.landmarks;

      const allPoints = Object.values(this.eyeLandmarksData).filter(
        (point) => Array.isArray(point) && point.length === 3,
      );
      if (
        this.$refs.vtkObjComponent &&
        typeof this.$refs.vtkObjComponent.updateLandmarks === "function"
      ) {
        this.$refs.vtkObjComponent.updateLandmarks(allPoints);
      } else {
        console.warn(
          "vtkObjComponent or updateLandmarks method is not available",
        );
      }

      this.snapshot_images = [];
      this.vtkComponentKey += 1;

      await this.$nextTick();

      if (
        this.$refs.vtkObjComponent &&
        typeof this.$refs.vtkObjComponent.captureSnapshots === "function"
      ) {
        this.$refs.vtkObjComponent.captureSnapshots();
      } else {
        console.warn(
          "vtkObjComponent or captureSnapshots method is not available",
        );
      }
      const FaceAlignmentRecord = this.allLandmarkRecords.find(
        (rec) => rec.algorithm === "FaceAlignment",
      );
      const FaceAlignmentId = FaceAlignmentRecord
        ? FaceAlignmentRecord.id
        : record.id;

      // Generate head views for the selected landmark
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      try {
        const response = await fetch(
          `${fasdBackendURL}/api/landmarks/generateHeadviewsFor/${FaceAlignmentId}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(this.eyeLandmarksData),
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const headViewData = await response.json();
        console.log("Head View Data:", headViewData);

        this.view_right_eye = headViewData.view_right_eye;
        this.view_left_eye = headViewData.view_left_eye;
      } catch (err) {
        console.error("Error generating head views for selected record", err);
        this.view_right_eye = null;
        this.view_left_eye = null;
      }

      // Display the VTK OBJ file
      this.displayFetchObjFile = true;
      this.$forceUpdate();

      // Automatically trigger PFL after 3D OBJ
      if (this.pflAnalysisResult) {
        return;
      }
      await this.onPFLClick();
    },

    startLandmarkProcess() {
      this.showProgressStatus = true;
      this.showLoadingLandMark = true;
      this.hideLandmarksUntilDone = true;
      this.initialLandmarksDone = false;
      this.meshmonkLandmarksDone = false;
      this.errorMessage = "";
      setTimeout(() => {
        this.onLandmarksClick();
      }, 10);
    },

    async onLandmarksClick() {
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        // Send a POST request to generate landmarks
        const createResponse = await fetch(
          `${fasdBackendURL}/api/landmarks/computeCNN20ptLandmarksFor/${this.facescan_id}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!createResponse.ok) {
          if (createResponse.status === 404) {
            this.errorMessage = "Landmarking server is not reachable.";
            return;
          }
        } else {
          this.initialLandmarksDone = true;
          this.initialLandmarkMessage =
            "Initial landmarks generated successfully!";
        }

        // Fetch updated landmark records
        await this.fetchLandmarksData();

        // start meshmonk process
        this.statusMessageLandmarks = "Generating Meshmonk face map...";
        const FaceAlignmentRecord = this.allLandmarkRecords.find(
          (rec) => rec.algorithm === "FaceAlignment",
        );

        if (!FaceAlignmentRecord) {
          this.errorMessage = "FaceAlignment landmark not found.";
          return;
        }

        // compute Meshmonk landmarks
        const landmarkId = FaceAlignmentRecord.id;

        // progress bar for Meshmonk
        this.progressMeshmonk = 0;
        let estimatedDuration = 90 * 1000;
        let updateInterval = 500;

        let steps = estimatedDuration / updateInterval;
        let increment = 100 / steps;

        this.progressInterval = setInterval(() => {
          if (this.progressMeshmonk < 100) {
            this.progressMeshmonk += increment;
          } else {
            clearInterval(this.progressInterval);
          }
        }, updateInterval);
        try {
          const meshmonkResponse = await fetch(
            `${fasdBackendURL}/api/landmarks/computeMeshmonkLandmarks/${landmarkId}`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            },
          );

          if (!meshmonkResponse.ok) {
            throw new Error("Meshmonk response has a problem");
          }

          this.meshmonkLandmarksDone = true;
          this.meshmonkMessage = "Meshmonk face map generated successfully!";
          await this.fetchLandmarksData();
        } catch (meshmonkErr) {
          console.error("Meshmonk error:", meshmonkErr);
          this.meshmonkMessage = "Meshmonk face map generation failed.";
        }
        // Automatically display the 3D OBJ with Eye Landmarks after landmark generation
        const eyeLandmarkRecord = this.allLandmarkRecords.find(
          (rec) => rec.algorithm === "CNNeyeZeyu",
        );

        if (eyeLandmarkRecord) {
          await this.onSelectLandmark(eyeLandmarkRecord);
        }

        this.statusMessageLandmarks = "";
        this.hideLandmarksUntilDone = false;
      } catch (error) {
        console.error("Error:", error);
        this.errorMessage = error.message || "Unexpected error occurred.";
      } finally {
        this.showLoadingLandMark = false;
        clearInterval(this.progressInterval);
        this.progressMeshmonk = 100;
      }
    },

    async fetchLandmarksData() {
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");
        const response = await fetch(
          `${fasdBackendURL}/api/landmarks/landmarkRecords/${this.facescan_id}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Error fetching landmarks: ${response.status}`);
        }

        const data = await response.json();

        this.allLandmarkRecords = data.map((record) => ({
          id: record.id,
          created_at: record.created_at,
          algorithm: record.algorithm,
          landmarks: record.landmarks,
        }));

        this.landmarkRecords = this.allLandmarkRecords.filter((record) => {
          const alg = record.algorithm.toLowerCase();
          return !alg.includes("facealignment") && !alg.includes("consensus");
        });

        this.showLandmarksRecord = this.landmarkRecords.length > 0;

        // Cache it + log
        const dp = useDPStore();
        dp.setCache(this.facescan_id, {
          landmarkRecords: this.allLandmarkRecords,
          cachedAt: Date.now(),
        });
        console.log(`[CACHE WRITE] `);
      } catch (error) {
        console.error("Error fetching landmarks:", error);
      }
    },

    getAlgorithmDisplayName(algorithm) {
      if (algorithm === "CNNeyeZeyu") return "Eye Landmarks";
      return algorithm;
    },
    async deleteLandmark(landmarkId) {
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");

        const response = await fetch(
          `${fasdBackendURL}/api/landmarks/${landmarkId}/`,
          {
            method: "DELETE",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Error deleting landmark: ${response.status}`);
        }
        this.landmarkRecords = this.landmarkRecords.filter(
          (record) => record.id !== landmarkId,
        );
        this.allLandmarkRecords = this.allLandmarkRecords.filter(
          (r) => r.id !== landmarkId,
        );
        // update cache after deletion
        const dp = useDPStore();
        dp.setCache(this.facescan_id, {
          landmarkRecords: this.allLandmarkRecords,
          cachedAt: Date.now(),
        });
      } catch (error) {
        console.error("Error deleting landmark:", error);
      }
    },

    ///////////////////////////////////////////////////////////////Heatmap////////////////////////////////////////////////////////////
    async fetchExistingSignatureId(landmarkId) {
      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      const response = await fetch(
        `${fasdBackendURL}/api/signatures/${landmarkId}/`,
        {
          method: "GET",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (!response.ok) return null;
      const data = await response.json();
      return Array.isArray(data) && data.length ? data[0].id : null;
    },

    async getHeatmapData() {
      if (this.requestInProgress) {
        console.warn("generateHeatmap is already in progress.");
        return null;
      }

      const fasdBackendURL =
        localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
      const token = localStorage.getItem("userJWtoken");

      // Clear previous heatmap results
      this.polydataXML = null;
      this.heatmapSnapshot = null;

      let chosenLandmarks = null;
      if (
        this.landmarkRecords.some((rec) =>
          rec.algorithm.includes("USER_APPROVED"),
        )
      ) {
        chosenLandmarks = this.landmarkRecords.find((rec) =>
          rec.algorithm.includes("USER_APPROVED"),
        );
      } else if (
        this.landmarkRecords.some((rec) => rec.algorithm.includes("Meshmonk"))
      ) {
        chosenLandmarks = this.landmarkRecords.find((rec) =>
          rec.algorithm.includes("Meshmonk"),
        );
      } else if (
        this.landmarkRecords.some((rec) => rec.algorithm.includes("consensus"))
      ) {
        chosenLandmarks = this.landmarkRecords.find((rec) =>
          rec.algorithm.includes("consensus"),
        );
      }

      this.chosenLandmarkId = chosenLandmarks ? chosenLandmarks.id : null;

      if (!this.chosenLandmarkId) {
        this.ErrorAlert.show = true;
        this.ErrorAlert.message =
          "Unable to compute the heatmap due to insufficient landmarks.";
        this.requestInProgress = false;
        return null;
      }
      // ----- CACHE CHECK -----
      const HEATMAP_TTL_MS = 15 * 60 * 1000;
      const dp = useDPStore();
      const facescanKey = String(this.facescan_id);
      const landmarkKey = String(this.chosenLandmarkId);
      const cached = dp.getHeatmap(facescanKey, landmarkKey);
      const age = cached?.cachedAt ? Date.now() - cached.cachedAt : null;
      const fresh = !!(cached?.cachedAt && age < HEATMAP_TTL_MS);

      if (fresh && cached.polydataXML) {
        console.log("[HEATMAP CACHE HIT]");

        this.polydataXML = cached.polydataXML;
        if (cached.snapshot) this.heatmapSnapshot = cached.snapshot;
        return cached.polydataXML;
      } else {
        console.log("[HEATMAP CACHE MISS]");
      }

      try {
        this.showLoadingHeatmap = true;
        this.requestInProgress = true;

        // check the existing signature id
        let signatureId = await this.fetchExistingSignatureId(
          this.chosenLandmarkId,
        );
        // POST request to get signatureId
        if (!signatureId) {
          const postResponse = await fetch(
            `${fasdBackendURL}/api/signatures/compute/${this.chosenLandmarkId}/`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({}),
            },
          );

          if (!postResponse.ok) {
            const errorDetail = await postResponse.json();
            if (postResponse.status === 422 && errorDetail.detail) {
              const missingLandmarks =
                errorDetail.detail.missingLandmarks?.join(", ") || "Unknown";

              this.ErrorAlert.show = true;
              this.ErrorAlert.message = `Heatmap computation failed due to missing landmarks: ${missingLandmarks}`;
            } else {
              this.ErrorAlert.show = true;
              this.ErrorAlert.message = `Heatmap computation failed: ${postResponse.statusText}`;
            }
            throw new Error(
              `POST request for heatmap computation failed: ${postResponse.status}`,
            );
          }

          const postResult = await postResponse.json();
          signatureId = postResult;

          if (!signatureId) {
            throw new Error("Received undefined signature_id");
          }
        }
        // GET request to retrieve polydata XML
        const getResponse = await fetch(
          `${fasdBackendURL}/api/signatures/heatmap/${signatureId}/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!getResponse.ok) {
          throw new Error(`GET request failed: ${getResponse.status}`);
        }

        // Store polydata
        const polydataXML = await getResponse.text();
        dp.setHeatmap(facescanKey, landmarkKey, {
          polydataXML,
          signatureId,
          cachedAt: Date.now(),
        });
        console.log("[HEATMAP CACHE WRITE]", { facescanKey, landmarkKey });

        return polydataXML;
      } catch (error) {
        console.error("Error generating heatmap:", error);
        if (!this.ErrorAlert.show) {
          this.ErrorAlert.show = true;
          this.ErrorAlert.message = "Heatmap computation failed!";
        }
        return null;
      } finally {
        this.requestInProgress = false;
        this.showLoadingHeatmap = false;
      }
    },

    async onGenerateHeatmap() {
      this.polydataXML = null;
      this.heatmapSnapshot = null;
      this.showHeatmap = false;
      this.ErrorAlert.show = false;

      this.showLoadingHeatmap = true;
      const polydataXML = await this.getHeatmapData();
      if (polydataXML) {
        this.polydataXML = polydataXML;
        this.showHeatmap = true;

        // Capture heatmap snapshot
        this.$nextTick(async () => {
          const heatmapImage =
            await this.$refs.heatmapComponentRef.captureHeatmapAsImage();
          if (heatmapImage) {
            this.heatmapSnapshot = heatmapImage;
          }
        });
      } else {
        console.error("Failed to retrieve polydata XML.");
      }
      this.displayGenerateMode1Plot = true;
      this.displayGenerateClassification = true;
      this.displayGenerateHeatMap = false;
      await this.onClassificationClick();
    },

    closeErrorAlert() {
      this.ErrorAlert.show = false;
    },
    //////////////////////////////////////////////////////////Classification//////////////////////////////////////////////////////////
    async onClassificationClick() {
      const CLASSIFICATION_TTL_MS = 15 * 60 * 1000;
      this.showLoadingClassification = true;
      this.showClassification = false;

      const landmarkId = this.chosenLandmarkId;
      if (!landmarkId) {
        this.showLoadingClassification = false;
        return;
      }

      const dp = useDPStore();
      const facescanKey = String(this.facescan_id);
      const landmarkKey = String(landmarkId);
      const cached = dp.getClassification(facescanKey, landmarkKey);
      const age = cached?.cachedAt ? Date.now() - cached.cachedAt : null;
      const fresh = !!(cached?.cachedAt && age < CLASSIFICATION_TTL_MS);

      if (fresh && (cached.result || cached.snapshot)) {
        console.log("[CLASSIFICATION CACHE HIT]");

        if (
          cached.result &&
          this.$refs.ClassificationComponentRef?.renderFromCache
        ) {
          await this.$refs.ClassificationComponentRef.renderFromCache(
            cached.result,
          );
        }
        if (cached.snapshot) {
          this.classificationSnapshot = cached.snapshot;
        }

        this.showLoadingClassification = false;
        this.showClassification = true;
        this.displayGenerateClassification = false;
        await this.onMode1PlotClick();
        return;
      }
      let result =
        await this.$refs.ClassificationComponentRef.fetchClassifications();

      if (!result && this.$refs.ClassificationComponentRef?.getResults) {
        result = this.$refs.ClassificationComponentRef.getResults();
      }

      this.showLoadingClassification = false;
      this.showClassification = true;
      this.displayGenerateClassification = false;

      dp.setClassification(this.facescan_id, landmarkId, {
        result: result || null,
        snapshot: this.classificationSnapshot || null, //
        cachedAt: Date.now(),
      });
      console.log("[CLASSIFICATION CACHE WRITE]");

      await this.onMode1PlotClick();
    },

    updateClassificationSnapshot(snapshot) {
      this.classificationSnapshot = snapshot;
    },
    handleRegionFetched(message) {
      this.successMessageClassification = message;
      setTimeout(() => {
        this.successMessageClassification = null;
      }, 3000);
    },
    updateClassificationMeans(means) {
      this.classificationMeans = means;
    },
    /////////////////////////////////////////////////////////Mode1Plot////////////////////////////////////////////////////////////////
    onMode1PlotClick() {
      console.log("Mode1plot button clicked");
      this.showMode1Plot = true;
      this.initMode1Plot();
      this.displayGenerateMode1Plot = false;
    },

    initMode1Plot() {
      this.$refs.Mode1PlotComponentRef.getPrincipalComponentData();
      this.$refs.Mode1PlotComponentRef.getMode1PlotData();
      this.$refs.Mode1PlotComponentRef.handleChart();
    },
    updateMode1PlotImage(event) {
      this.mode1PlotImage = event;
    },
    Mode1Parameters(data) {
      this.firstPrincipalComponent = data.firstPrincipalComponent;
      this.mode1Percentile = data.mode1Percentile;
    },

    ///////////////////////////////////////////////////Report////////////////////////////////////////////////////////////
    onMainReportClick() {
      this.showMainReportLoading = true;
      setTimeout(() => {
        this.showMainReport = true;
        this.showMainReportLoading = false;
        (this.displayDownloadPDF = true),
          this.$nextTick(() => {
            this.init3DViewer();
          });
      }, 3000);
    },
    generatePDF() {
      this.$refs.generatepdf.generatePDF();
    },
    generateExcel() {
      this.$refs.generateexcel.generateExcel();
    },
  },
};
</script>

<style scoped>
.data-processing-container {
  padding: 0 20px 20px 20px;
  font-family: Arial, sans-serif;
}

.texture-container img {
  max-width: 100%;
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}
.loading-message {
  font-size: 16px;
  color: #007bff;
  margin-top: 20px;
}

.error-message {
  font-size: 16px;
  color: #dc3545;
  margin-top: 20px;
}
.facescan-table thead {
  background-color: #004ead;
}

.facescan-table th {
  color: #ffffff;
  text-align: left;
  padding: 10px;
}
.landmarktitle {
  font-size: 1rem;
}
.delete-icon-landmark {
  color: #dc3545;
  cursor: pointer;
  margin-left: auto;
  transition: color 0.2s ease;
}
.delete-icon-landmark:hover {
  color: #bd2130;
}
.buttonDataProcessing:hover {
  background-color: #002d64;
}
.buttonDataProcessing {
  margin: 0 5px;
  color: white;
  padding: 10px 20px;
  border-radius: 5px;
  background-color: #004ead;
  color: white;
  font-weight: bold;
  border: none;
}
.spinner-borderDataProcessing {
  display: inline-block;
  width: 1.25rem;
  height: 1.25rem;
  vertical-align: text-bottom;
  border: 0.15em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
  color: #d5d9de !important;
}

@keyframes spinner-borderDataProcessing {
  100% {
    transform: rotate(360deg);
  }
}
.success-messageClassification {
  margin-top: 10px;
  background: linear-gradient(90deg, #28a745, #34d058);
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  animation: fadeInOut 3s ease-in-out;
}

.success-messageClassification .icon {
  width: 18px;
  height: 18px;
  background-color: #fff;
  color: #31cb55;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
}

@keyframes fadeInOut {
  0% {
    opacity: 0;
    transform: translateY(-10px);
  }
  10% {
    opacity: 1;
    transform: translateY(0);
  }
  90% {
    opacity: 1;
    transform: translateY(0);
  }
  100% {
    opacity: 0;
    transform: translateY(10px);
  }
}
.download-btn-container {
  justify-content: center;
  margin-top: 20px;
  gap: 10px;
  display: flex;
}
.download-btn {
  display: inline-block;
  padding: 10px 20px;
  font-size: 16px;
  background-color: #004ead;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-top: 10px;
}
.download-btn:focus {
  outline: none;
}

.download-btn:hover {
  background-color: #0056b3;
}
.heatmap_error {
  color: #b80000;
}
.buttonDataProcessing {
  padding: 10px 20px;
  font-weight: bold;
  font-size: 1rem;
}
.fa-spinner-icon {
  color: #004ead !important;
}
</style>
